import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchSgCalendar } from './sgCalendarApi';
import {
  KIND_BADGE_CLASS,
  KIND_LABEL,
  buildEventPath,
  buildMonthGrid,
  formatEventDate,
  monthRange,
  toDateKey,
} from './sgCalendarUtils';
import type { SgCalendarEvent, SgCalendarEventKind } from './types';

const KINDS = Object.keys(KIND_LABEL) as SgCalendarEventKind[];
const WEEKDAYS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

type ViewMode = 'month' | 'list';

export function SgCalendarView() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [kind, setKind] = useState('');
  const [events, setEvents] = useState<SgCalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const range = monthRange(year, month);
      const { events: data } = await fetchSgCalendar({
        from: range.from,
        to: range.to,
        kind: (kind || undefined) as SgCalendarEventKind | undefined,
      });
      setEvents(data);
    } catch {
      setError('No se pudo cargar el calendario del Sistema de Gestión.');
    } finally {
      setLoading(false);
    }
  }, [year, month, kind]);

  useEffect(() => {
    void load();
  }, [load]);

  const eventsByDay = useMemo(() => {
    const map = new Map<string, SgCalendarEvent[]>();
    for (const event of events) {
      const key = toDateKey(event.date);
      const bucket = map.get(key) ?? [];
      bucket.push(event);
      map.set(key, bucket);
    }
    return map;
  }, [events]);

  const monthCells = useMemo(() => buildMonthGrid(year, month), [year, month]);

  const monthLabel = new Date(year, month, 1).toLocaleDateString('es-AR', {
    month: 'long',
    year: 'numeric',
  });

  const goPrevMonth = () => {
    if (month === 0) {
      setYear((value) => value - 1);
      setMonth(11);
      return;
    }
    setMonth((value) => value - 1);
  };

  const goNextMonth = () => {
    if (month === 11) {
      setYear((value) => value + 1);
      setMonth(0);
      return;
    }
    setMonth((value) => value + 1);
  };

  return (
    <div className='space-y-6' data-testid='sg-calendar'>
      <div>
        <h1 className='text-2xl font-bold text-foreground'>Calendario del Sistema de Gestión</h1>
        <p className='text-sm text-muted-foreground mt-1'>
          Hitos planificados: auditorías, legales, objetivos, capacitaciones y revisiones.
        </p>
      </div>

      <div className='flex flex-wrap gap-3 items-end'>
        <div className='flex items-center gap-2'>
          <button
            type='button'
            className='rounded-md border px-3 py-2 text-sm hover:bg-muted'
            onClick={goPrevMonth}
            aria-label='Mes anterior'
          >
            ←
          </button>
          <span className='text-sm font-medium min-w-[10rem] text-center capitalize'>{monthLabel}</span>
          <button
            type='button'
            className='rounded-md border px-3 py-2 text-sm hover:bg-muted'
            onClick={goNextMonth}
            aria-label='Mes siguiente'
          >
            →
          </button>
        </div>

        <label className='flex flex-col gap-1 text-sm'>
          <span className='text-muted-foreground'>Tipo</span>
          <select
            className='rounded-md border bg-background px-3 py-2 min-w-[12rem]'
            value={kind}
            onChange={(event) => setKind(event.target.value)}
          >
            <option value=''>Todos</option>
            {KINDS.map((value) => (
              <option key={value} value={value}>
                {KIND_LABEL[value]}
              </option>
            ))}
          </select>
        </label>

        <div className='flex rounded-md border overflow-hidden text-sm'>
          <button
            type='button'
            className={`px-3 py-2 ${viewMode === 'month' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
            onClick={() => setViewMode('month')}
          >
            Mes
          </button>
          <button
            type='button'
            className={`px-3 py-2 ${viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
            onClick={() => setViewMode('list')}
          >
            Lista
          </button>
        </div>
      </div>

      {loading && (
        <p className='text-sm text-muted-foreground' data-testid='sg-calendar-loading'>
          Cargando eventos…
        </p>
      )}

      {error && (
        <div className='rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive'>
          {error}
        </div>
      )}

      {!loading && !error && viewMode === 'month' && (
        <div className='rounded-lg border overflow-hidden'>
          <div className='grid grid-cols-7 bg-muted/50 text-xs font-medium text-muted-foreground'>
            {WEEKDAYS.map((day) => (
              <div key={day} className='px-2 py-2 text-center border-b'>
                {day}
              </div>
            ))}
          </div>
          <div className='grid grid-cols-7'>
            {monthCells.map((cell, index) => {
              if (!cell) {
                return <div key={`empty-${index}`} className='min-h-[6rem] border-b border-r bg-muted/20' />;
              }
              const key = toDateKey(cell.toISOString());
              const dayEvents = eventsByDay.get(key) ?? [];
              const isToday = toDateKey(today.toISOString()) === key;

              return (
                <div
                  key={key}
                  className={`min-h-[6rem] border-b border-r p-1 align-top ${isToday ? 'bg-primary/5' : ''}`}
                >
                  <div className={`text-xs font-medium mb-1 ${isToday ? 'text-primary' : ''}`}>
                    {cell.getDate()}
                  </div>
                  <ul className='space-y-1'>
                    {dayEvents.slice(0, 3).map((event) => (
                      <li key={`${event.kind}-${event.refId}-${event.date}`}>
                        <Link
                          to={buildEventPath(event)}
                          className={`block rounded px-1 py-0.5 text-[10px] leading-tight truncate ${KIND_BADGE_CLASS[event.kind]}`}
                          title={event.title}
                        >
                          {event.title}
                        </Link>
                      </li>
                    ))}
                    {dayEvents.length > 3 && (
                      <li className='text-[10px] text-muted-foreground px-1'>+{dayEvents.length - 3} más</li>
                    )}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!loading && !error && viewMode === 'list' && (
        <div className='rounded-lg border overflow-hidden'>
          {events.length === 0 ? (
            <p className='p-4 text-sm text-muted-foreground'>No hay eventos en este período.</p>
          ) : (
            <table className='w-full text-sm'>
              <thead className='bg-muted/50 text-left'>
                <tr>
                  <th className='px-4 py-2 font-medium'>Fecha</th>
                  <th className='px-4 py-2 font-medium'>Tipo</th>
                  <th className='px-4 py-2 font-medium'>Hito</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <tr key={`${event.kind}-${event.refId}-${event.date}`} className='border-t'>
                    <td className='px-4 py-2 whitespace-nowrap'>{formatEventDate(event.date)}</td>
                    <td className='px-4 py-2'>
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs ${KIND_BADGE_CLASS[event.kind]}`}
                      >
                        {KIND_LABEL[event.kind]}
                      </span>
                    </td>
                    <td className='px-4 py-2'>
                      <Link to={buildEventPath(event)} className='text-primary hover:underline'>
                        {event.title}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
